import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import {
  collection, query, orderBy, onSnapshot, deleteDoc, doc,
} from 'firebase/firestore';
import {
  Box, Typography, Button, Paper, IconButton, Chip, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GridViewIcon from '@mui/icons-material/GridView';
import { QRCodeSVG } from 'qrcode.react';

const accent = '#E05500';

export function SwotNLogo({ variant = 'h5' }) {
  return (
    <Typography variant={variant} fontWeight={700} component="span">
      Swot<span style={{ color: accent }}>N</span>
    </Typography>
  );
}

const QUAD_COLORS = {
  strength:    { label: 'Styrkor',     color: '#4CAF50', letter: 'S' },
  weakness:    { label: 'Svagheter',   color: '#EF5350', letter: 'W' },
  opportunity: { label: 'Möjligheter', color: '#42A5F5', letter: 'O' },
  threat:      { label: 'Hot',         color: '#E05500', letter: 'T' },
};

export default function SwotNAdmin() {
  const navigate   = useNavigate();
  const [sessions, setSessions]     = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [deleteId,  setDeleteId]  = useState(null);
  const [qrSession,   setQrSession]   = useState(null); // { id, title }
  const [qrCopied,    setQrCopied]    = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'swot_sessions'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, []);

  const confirmDelete = async () => {
    if (!deleteId) return;
    await deleteDoc(doc(db, 'swot_sessions', deleteId));
    setDeleteId(null);
  };

  const statusLabel = (s) => {
    if (s === 'collecting') return { label: 'Insamling', color: '#4CAF50' };
    if (s === 'reviewing')  return { label: 'Granskning', color: '#FF9800' };
    return { label: 'Klar', color: '#42A5F5' };
  };

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <SwotNLogo />
          <Typography color="text.secondary" fontSize="0.82rem" mt={0.3}>
            Digital SWOT-analys
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/swotn/new')}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
        >
          Ny SWOT
        </Button>
      </Box>

      {/* Intro när inga sessioner */}
      {sessions.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <GridViewIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography fontWeight={600} mb={1}>Inga SWOT-analyser ännu</Typography>
          <Typography color="text.secondary" fontSize="0.88rem" mb={3}>
            Skapa din första SWOT-analys eller importera från en WorkshopN-session.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/swotn/new')}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
          >
            Skapa SWOT-analys
          </Button>
        </Paper>
      )}

      {/* Session-lista */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sessions.map(s => {
          const st = statusLabel(s.status);
          return (
            <Paper key={s.id} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: accent + '66' }, transition: 'border-color 0.2s' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>

                {/* S-W-O-T miniatyr */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.4, flexShrink: 0 }}>
                  {Object.values(QUAD_COLORS).map(q => (
                    <Box key={q.letter} sx={{
                      width: 18, height: 18, borderRadius: 0.5,
                      bgcolor: q.color + '33', border: `1px solid ${q.color}55`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography sx={{ fontSize: '0.55rem', fontWeight: 900, color: q.color }}>{q.letter}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight={700} fontSize="0.95rem" noWrap>{s.title}</Typography>
                    <Chip
                      label={st.label} size="small"
                      sx={{ height: 18, fontSize: '0.62rem', bgcolor: st.color + '22', color: st.color, fontWeight: 600 }}
                    />
                    {s.mode === 'hybrid' && (
                      <Chip label="Hybrid" size="small" icon={<PrintIcon sx={{ fontSize: '0.7rem !important' }} />}
                        sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#1565c022', color: '#42A5F5', fontWeight: 600 }} />
                    )}
                    {s.workshopId && (
                      <Chip label="WorkshopN" size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: accent + '22', color: accent, fontWeight: 600 }} />
                    )}
                  </Box>
                  {s.question && (
                    <Typography fontSize="0.8rem" color="text.secondary" mt={0.25} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.question}
                    </Typography>
                  )}
                  <Typography fontSize="0.7rem" color="text.disabled" mt={0.5}>
                    {s.createdByName || ''} · {s.createdAt?.toDate?.()?.toLocaleDateString('sv-SE') || ''}
                    {s.cardCount > 0 && ` · ${s.cardCount} kort`}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Inbjudnings-QR">
                    <IconButton
                      onClick={() => setQrSession({ id: s.id, title: s.title })}
                      sx={{ color: 'text.secondary', '&:hover': { color: '#42A5F5', bgcolor: '#42A5F511' } }}
                    >
                      <QrCode2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Redigera">
                    <IconButton
                      onClick={() => navigate(`/swotn/${s.id}/edit`)}
                      sx={{ color: 'text.secondary', '&:hover': { color: accent, bgcolor: accent + '11' } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Öppna board">
                    <IconButton
                      onClick={() => navigate(`/swotn/${s.id}`)}
                      sx={{ color: accent, '&:hover': { bgcolor: accent + '11' } }}
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ta bort">
                    <IconButton
                      onClick={() => setDeleteId(s.id)}
                      sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Bekräfta radering */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Ta bort SWOT-analys?</DialogTitle>
        <DialogContent>
          <Typography fontSize="0.88rem" color="text.secondary">
            Analysen och alla kort raderas permanent och kan inte återställas.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Avbryt</Button>
          <Button onClick={confirmDelete} sx={{ color: '#ef4444', textTransform: 'none', fontWeight: 600 }}>
            Ja, ta bort
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR-kod för inbjudan */}
      <Dialog open={!!qrSession} onClose={() => setQrSession(null)} maxWidth="xs">
        <DialogTitle sx={{ pb: 0.5 }}>
          Dela SWOT-board
        </DialogTitle>
        <DialogContent>
          <Typography fontSize="0.8rem" color="text.secondary" mb={2}>
            {qrSession?.title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              {qrSession && (
                <QRCodeSVG
                  value={`${window.location.origin}/swotn/${qrSession.id}`}
                  size={200}
                  level="M"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{
                flex: 1, px: 1.5, py: 1, bgcolor: 'action.hover',
                borderRadius: 1, fontSize: '0.7rem', color: 'text.secondary',
                fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {qrSession ? `${window.location.origin}/swotn/${qrSession.id}` : ''}
              </Box>
              <Tooltip title={qrCopied ? 'Kopierat!' : 'Kopiera länk'}>
                <IconButton size="small" onClick={() => {
                  if (!qrSession) return;
                  navigator.clipboard.writeText(`${window.location.origin}/swotn/${qrSession.id}`);
                  setQrCopied(true); setTimeout(() => setQrCopied(false), 2500);
                }} sx={{ color: qrCopied ? 'success.main' : accent }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setQrSession(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Stäng</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

